import React from 'react';
import { Button, Form, Input, message, Radio, Space } from 'antd';
import { WorkspaceItemDto } from '@api/Api';
import {
  useCreateTemplate,
  useUpdateTemplate
} from '@hooks/queries/awTemplate';
import UpgradePlanTooltip from '@components/Tooltip/UpgradePlanTooltip';
import type { AwState } from '@components/Modals/ApprovalModal/ApprovalModal/ApprovalModal';
import { DiamondUpgradeIcon } from '@icons';
import './ManageTemplate.less';

type UpdateTemplateProps = {
  onCancel: () => void;
  onFinish: (templateId?: string) => void;
  templateId: string;
  limitTemplateReached?: boolean;
  awState: AwState;
  currentWorkspace: WorkspaceItemDto;
  initialValues?: FormValues;
};

type FormValues = {
  saveAsTemplate: string;
  templateName: string;
};

export default function UpdateTemplate({
  onCancel,
  onFinish,
  awState,
  currentWorkspace,
  limitTemplateReached = false,
  initialValues = {
    saveAsTemplate: 'miss',
    templateName: ''
  },
  templateId
}: UpdateTemplateProps) {
  const { mutateAsync: createTemplate, isPending: isLoadingCreateTemplate } =
    useCreateTemplate({});
  const { mutateAsync: updateTemplate, isPending: isLoadingUpdateTemplate } =
    useUpdateTemplate({});
  const [form] = Form.useForm<FormValues>();
  const saveAsTemplate = Form.useWatch('saveAsTemplate', form);

  const handlerError = (error?: string) => {
    if (error === 'aw_template_name_already_exists') {
      form.setFields([
        {
          name: 'templateName',
          errors: ['Template with this name already exists']
        }
      ]);
      return;
    }
    message.error({
      content: error || 'Something went wrong'
    });
  };

  const onSaveTemplate = async (values: FormValues) => {
    try {
      if (!currentWorkspace?.id) return;
      if (values.saveAsTemplate === 'miss' || !templateId) {
        onFinish();
        return;
      }
      const data = {
        workspaceId: currentWorkspace.id,
        lockDecisions: awState.lockDecisions,
        disableNonApprovedDownloads: awState.disableNonApprovedDownloads,
        newVersionBehavior: awState.newVersionBehavior,
        password: awState.password,
        customMessage: awState.customMessage,
        allowAllVersions: awState.allowAllVersions,
        allowAllComments: awState.allowAllComments,
        stages: (awState.stages || []).map((item) => ({
          name: item.name,
          completionCondition: item.completionCondition,
          members: item.members.map((member) => ({
            email: member.email,
            canComment: member.canComment,
            canSubmitDecision: member.canSubmitDecision,
            canUploadNewVersion: member.canUploadNewVersion,
            canManageAW: member.canManageAW,
            canDownload: member.canDownload,
            hasCommentPriority: member.hasCommentPriority,
            hasDominatingDecision: member.hasDominatingDecision
          }))
        }))
      };
      if (values.saveAsTemplate === 'update') {
        const resTemplate = await updateTemplate({
          id: templateId,
          ...data
        });
        onFinish(resTemplate.data.template.id);
        return;
      }
      if (values.saveAsTemplate === 'create') {
        const resTemplate = await createTemplate({
          ...data,
          name: values.templateName
        });
        if ('errorCode' in resTemplate.data) {
          handlerError(resTemplate.data.errorCode as string);
          return;
        }
        onFinish(resTemplate.data.template.id);
      }
    } catch (error: any) {
      handlerError(error?.response?.data?.errorCode as string);
    }
  };

  return (
    <div className="modal_container manage-template">
      <h2
        className="modal_container__title"
        style={{ fontSize: 22, marginBottom: '16px' }}
      >
        Save template changes?
      </h2>
      <div
        className="modal_container__text"
        style={{ fontSize: 14, marginBottom: '32px' }}
      >
        You made changes to approval routing, that was created from a template.
        <br />
        How would you like to proceed with the template itself?
      </div>
      <Form
        form={form}
        layout="vertical"
        initialValues={initialValues}
        onFinish={onSaveTemplate}
      >
        <Form.Item name="saveAsTemplate">
          <Radio.Group size="small">
            <Space direction="vertical" size="middle">
              <Radio value="miss">Discard changes</Radio>
              <Radio value="update">Save changes to existing template</Radio>
              <Radio value="create" disabled={limitTemplateReached}>
                <UpgradePlanTooltip
                  text=" to enterprise plan to unlock additional approval routing templates."
                  isCanVisible={limitTemplateReached}
                  placement="right"
                  innerStyle={{
                    maxWidth: '280px'
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      gap: '4px',
                      alignItems: 'center'
                    }}
                  >
                    Save as a new template
                    {limitTemplateReached && (
                      <DiamondUpgradeIcon className="upgrade-icon" />
                    )}
                  </div>
                </UpgradePlanTooltip>
              </Radio>
            </Space>
          </Radio.Group>
        </Form.Item>
        {saveAsTemplate === 'create' && (
          <Form.Item
            label="Name approval routing template"
            name="templateName"
            rules={[{ required: true, message: 'Name required.' }]}
          >
            <Input size="large" />
          </Form.Item>
        )}
        <Space
          size="large"
          style={{ justifyContent: 'flex-end', width: '100%' }}
        >
          <Button onClick={onCancel} type="text">
            Cancel
          </Button>
          <Button
            loading={isLoadingCreateTemplate || isLoadingUpdateTemplate}
            type="primary"
            htmlType="submit"
            style={{ minWidth: 152 }}
          >
            Done
          </Button>
        </Space>
      </Form>
    </div>
  );
}
